import React, { useState, useRef, useCallback } from 'react';
import { Mic, Volume2, Loader2, Square } from 'lucide-react';
import { Button } from '../ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Encode raw PCM Float32 samples into a WAV file Blob.
 * WAV is universally supported by Whisper — no codec issues on any platform.
 */
function encodeWAV(samples, sampleRate) {
  const buffer = new ArrayBuffer(44 + samples.length * 2);
  const view = new DataView(buffer);

  function writeString(offset, string) {
    for (let i = 0; i < string.length; i++) {
      view.setUint8(offset + i, string.charCodeAt(i));
    }
  }

  // WAV header
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + samples.length * 2, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // subchunk size
  view.setUint16(20, 1, true);  // PCM
  view.setUint16(22, 1, true);  // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true);  // block align
  view.setUint16(34, 16, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, samples.length * 2, true);

  // Convert float32 to int16
  for (let i = 0; i < samples.length; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }

  return new Blob([buffer], { type: 'audio/wav' });
}

const VoiceControls = ({ onTranscription, onError, textToSpeak, compact = false }) => {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [status, setStatus] = useState(''); // visible status text
  const audioContextRef = useRef(null);
  const sourceRef = useRef(null);
  const processorRef = useRef(null);
  const streamRef = useRef(null);
  const samplesRef = useRef([]);
  const audioRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        onError?.('Microphone not supported in this browser.');
        return;
      }

      setStatus('Requesting mic...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      streamRef.current = stream;

      // Use Web Audio API to capture raw PCM — works reliably on all platforms including iOS
      const audioContext = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: 16000 });
      audioContextRef.current = audioContext;

      // iOS requires resuming AudioContext after creation
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      const source = audioContext.createMediaStreamSource(stream);
      sourceRef.current = source;

      // ScriptProcessorNode captures raw PCM samples
      const processor = audioContext.createScriptProcessor(4096, 1, 1);
      processorRef.current = processor;
      samplesRef.current = [];

      processor.onaudioprocess = (e) => {
        const inputData = e.inputBuffer.getChannelData(0);
        // Copy the samples (they get reused by the browser)
        samplesRef.current.push(new Float32Array(inputData));
      };

      source.connect(processor);
      processor.connect(audioContext.destination);

      setRecording(true);
      setStatus('Recording... tap Stop when done');
    } catch (err) {
      console.error('Mic access error:', err);
      setStatus('');
      if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
        onError?.('Microphone access denied. Please allow it in browser settings.');
      } else {
        onError?.('Could not access microphone: ' + err.message);
      }
    }
  }, [onError]);

  const stopRecording = useCallback(async () => {
    setRecording(false);

    // Disconnect audio nodes
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (sourceRef.current) {
      sourceRef.current.disconnect();
      sourceRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }

    const sampleRate = audioContextRef.current?.sampleRate || 16000;

    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }

    // Merge all sample chunks into one array
    const chunks = samplesRef.current;
    samplesRef.current = [];

    if (chunks.length === 0) {
      setStatus('');
      onError?.('No audio captured. Please try again.');
      return;
    }

    const totalLength = chunks.reduce((acc, c) => acc + c.length, 0);
    const allSamples = new Float32Array(totalLength);
    let offset = 0;
    for (const chunk of chunks) {
      allSamples.set(chunk, offset);
      offset += chunk.length;
    }

    // Check if the recording is too short (less than 0.5 seconds)
    if (totalLength < sampleRate * 0.5) {
      setStatus('');
      onError?.('Recording too short. Please speak for at least 1 second.');
      return;
    }

    // Encode to WAV
    const wavBlob = encodeWAV(allSamples, sampleRate);

    setTranscribing(true);
    setStatus('Transcribing your speech...');

    try {
      const formData = new FormData();
      formData.append('file', wavBlob, 'recording.wav');

      const res = await axios.post(`${BACKEND_URL}/api/voice/transcribe`, formData, {
        timeout: 30000,
      });

      const text = res.data?.text?.trim();
      if (text) {
        onTranscription?.(text);
        setStatus('');
      } else {
        setStatus('');
        onError?.('Could not understand the audio. Please speak more clearly.');
      }
    } catch (err) {
      console.error('Transcription error:', err);
      setStatus('');
      const detail = err.response?.data?.detail || err.message || '';
      onError?.(`Transcription failed${detail ? ': ' + detail : '. Please try again.'}`);
    } finally {
      setTranscribing(false);
    }
  }, [onTranscription, onError]);

  const speakText = useCallback(async (text) => {
    if (!text || speaking) return;
    setSpeaking(true);
    setStatus('Loading audio...');

    // On iOS, create Audio element immediately (in user tap context)
    const audio = new Audio();
    audioRef.current = audio;

    try {
      const res = await axios.post(`${BACKEND_URL}/api/voice/speak`, { text }, {
        responseType: 'arraybuffer',
        timeout: 30000,
      });
      const blob = new Blob([res.data], { type: 'audio/mpeg' });
      const url = URL.createObjectURL(blob);

      audio.src = url;
      audio.onended = () => {
        setSpeaking(false);
        setStatus('');
        URL.revokeObjectURL(url);
      };
      audio.onerror = () => {
        setSpeaking(false);
        setStatus('');
        onError?.('Audio playback failed.');
      };
      setStatus('Playing...');
      await audio.play();
    } catch (err) {
      console.error('TTS error:', err);
      setSpeaking(false);
      setStatus('');
      onError?.('Text-to-speech failed. Please try again.');
    }
  }, [speaking, onError]);

  const stopSpeaking = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
    }
    setSpeaking(false);
    setStatus('');
  }, []);

  // ─── Compact mode (used in AI Assistant chat bar) ───
  if (compact) {
    return (
      <div className="flex flex-col">
        {status && (
          <span className="text-[10px] text-slate-500 mb-1 px-0.5 leading-tight">{status}</span>
        )}
        <div className="flex items-center gap-1">
          {transcribing ? (
            <Button type="button" size="sm" disabled className="h-8 px-2 flex-shrink-0" data-testid="voice-transcribing">
              <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />
              <span className="text-[11px]">...</span>
            </Button>
          ) : recording ? (
            <Button
              type="button"
              size="sm"
              onClick={stopRecording}
              className="h-8 px-2.5 flex-shrink-0 bg-red-600 hover:bg-red-700 text-white animate-pulse"
              data-testid="voice-stop-recording"
            >
              <Square className="h-3 w-3 mr-1" />
              <span className="text-[11px] font-medium">Stop</span>
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={startRecording}
              className="h-8 w-8 p-0 flex-shrink-0"
              title="Speak your message"
              data-testid="voice-start-recording"
            >
              <Mic className="h-3.5 w-3.5" />
            </Button>
          )}

          {textToSpeak && (
            speaking ? (
              <Button
                type="button"
                size="sm"
                onClick={stopSpeaking}
                className="h-8 px-2.5 flex-shrink-0 bg-violet-600 hover:bg-violet-700 text-white animate-pulse"
                data-testid="voice-stop-speaking"
              >
                <Square className="h-3 w-3 mr-1" />
                <span className="text-[11px]">Stop</span>
              </Button>
            ) : (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => speakText(textToSpeak)}
                className="h-8 w-8 p-0 flex-shrink-0 text-violet-600 border-violet-300 hover:bg-violet-50"
                title="Listen to AI response"
                data-testid="voice-speak"
              >
                <Volume2 className="h-3.5 w-3.5" />
              </Button>
            )
          )}
        </div>
      </div>
    );
  }

  // ─── Full mode (used in AIApplicationFlow) ───
  return (
    <div className="flex flex-col">
      {status && (
        <span className="text-[11px] text-slate-500 mb-1 px-0.5">{status}</span>
      )}
      <div className="flex items-center gap-1.5">
        {transcribing ? (
          <Button type="button" size="sm" disabled className="h-9 px-3 flex-shrink-0" data-testid="voice-transcribing">
            <Loader2 className="h-4 w-4 animate-spin mr-1.5" />
            <span className="text-xs">Processing...</span>
          </Button>
        ) : recording ? (
          <Button
            type="button"
            size="sm"
            onClick={stopRecording}
            className="h-9 px-3 flex-shrink-0 bg-red-600 hover:bg-red-700 text-white animate-pulse"
            data-testid="voice-stop-recording"
          >
            <Square className="h-3.5 w-3.5 mr-1.5" />
            <span className="text-xs font-medium">Stop</span>
          </Button>
        ) : (
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={startRecording}
            className="h-9 px-3 flex-shrink-0"
            title="Speak your message"
            data-testid="voice-start-recording"
          >
            <Mic className="h-4 w-4 mr-1.5" />
            <span className="text-xs">Speak</span>
          </Button>
        )}

        {textToSpeak && (
          speaking ? (
            <Button
              type="button"
              size="sm"
              onClick={stopSpeaking}
              className="h-9 px-3 flex-shrink-0 bg-violet-600 hover:bg-violet-700 text-white animate-pulse"
              data-testid="voice-stop-speaking"
            >
              <Square className="h-3.5 w-3.5 mr-1.5" />
              <span className="text-xs">Stop</span>
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => speakText(textToSpeak)}
              className="h-9 px-3 flex-shrink-0 text-violet-600 border-violet-300 hover:bg-violet-50"
              title="Listen to AI response"
              data-testid="voice-speak"
            >
              <Volume2 className="h-4 w-4 mr-1.5" />
              <span className="text-xs">Listen</span>
            </Button>
          )
        )}
      </div>
    </div>
  );
};

export default VoiceControls;
